import { FilterItemResource } from '@appTypes/FilterTypes';
import { UserProfileFormResource } from '@appTypes/header/UserProfile';
import SingleSelectDropdown from '@components/shared/filters/single-select-dropdown/SingleSelectDropdown';
import ToggleSwitch from '@components/shared/toggle-switch/ToggleSwitch';
import { NotificationPreferenceType } from '@enums/NotificationPreferenceType';
import { useCheckDevice } from '@hooks/UseCheckDevice';
import { theme } from '@theme';
import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { RowDivider } from '../UserProfileStyles';

type MessageTextProps = {
  marginLeft?: number;
};

const MessageText: FC<MessageTextProps> = ({ marginLeft }) => {
  return (
    <MessageWrapper marginLeft={marginLeft}>
      Message and data rates apply for SMS notifications. To stop SMS notifications, text stop to
      374899 or de-select the notification setting in your user profile. See terms and conditions
      and privacy policy at automativemastermind.com. Message frequency depends on customer
      assignments and customer activity.
    </MessageWrapper>
  );
};

const UserNotificationsPreference = () => {
  const { isMobileDevice } = useCheckDevice();
  const { watch, getValues, setValue } = useFormContext<UserProfileFormResource>();
  const notificationsSettings = getValues('notifications');

  const [enableNotif, setEnableNotif] = useState<boolean>(
    notificationsSettings?.mobileNotificationsEnabled || false,
  );
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const [selectedStoreName, setSelectedStoreName] = useState<string | null>(null);

  const storesList: FilterItemResource[] =
    notificationsSettings?.preferences?.map((x) => ({
      key: x.storeId,
      label: x.storeName,
    })) || [];

  const storeChange = (e) => {
    setSelectedStoreName(e.label);
    setSelectedStoreId(e.key);
  };

  const preferences = watch('notifications.preferences');
  const isOptedOutSms = watch('isOptedOutSms');

  const updateCheckbox = (
    storeIndex: number,
    type: NotificationPreferenceType,
    option: string,
    isChecked: boolean,
  ) => {
    const updatedPreferences = isChecked
      ? [...preferences[storeIndex][type], option]
      : preferences[storeIndex][type]?.filter(
          (item) => item.toLowerCase() !== option.toLowerCase(),
        );
    setValue(`notifications.preferences.${storeIndex}.${type}`, updatedPreferences);
  };

  const toggleCallBack = () => {
    const updatedNotificationToggle = !enableNotif;
    setEnableNotif(updatedNotificationToggle);
    setValue('notifications.mobileNotificationsEnabled', updatedNotificationToggle);
  };

  const notificationOptions = [
    'Service Arrival',
    'Campaign Views',
    'Service-to-Sales',
    'Customer Greeted',
  ];

  return (
    <RowDivider
      borderColor={isMobileDevice ? theme.palette.backgroundGray : theme.palette.lightGreySix}
    >
      <div className='col-sm-12 col-xs-12'>
        <div className='form-group pb-4 mb-0'>
          <Header>
            <LabelWrapper>Mobile Notifications</LabelWrapper>
            <ToggleSwitch
              isActive={enableNotif}
              toggleCallBack={toggleCallBack}
              cssClass='notification-toggle'
            />
            {!enableNotif && (
              <MessageWrapper marginLeft={10}>Your notifications are paused.</MessageWrapper>
            )}
          </Header>
        </div>
      </div>
      {enableNotif && (
        <div className='col-sm-12 col-xs-12'>
          <div className='col-sm-6 col-xs-12'>
            <LeftWrapper isMobileDevice={isMobileDevice}>
              <SingleSelectDropdown
                placeholder='Select Store'
                options={storesList}
                onChange={storeChange}
                selectedLabelPosition='right'
                type='am-dropdown'
                cssClass='responsive-adjust'
                displayRightLabel
                rightLabel={storesList.length.toString()}
                closeAfterSelection={true}
              />
              {!isMobileDevice && <MessageText marginLeft={5} />}
            </LeftWrapper>
          </div>
          <div className='col-sm-6 col-xs-12'>
            {selectedStoreName && (
              <>
                <StoreNameWrapper isMobileDevice={isMobileDevice}>
                  {selectedStoreName}
                </StoreNameWrapper>
                {notificationsSettings?.preferences.map((pref, index) =>
                  pref.storeId === selectedStoreId ? (
                    <div key={pref.storeId}>
                      <Wrapper>
                        <CheckboxLabelWrapper></CheckboxLabelWrapper>
                        <CheckboxWrapper>
                          <div>SMS</div>
                          <div>Push</div>
                        </CheckboxWrapper>
                      </Wrapper>
                      {notificationOptions.map((option) => (
                        <Wrapper key={option}>
                          <CheckboxLabelWrapper>{option}</CheckboxLabelWrapper>
                          <CheckboxWrapper>
                            <label>
                              <input
                                type='checkbox'
                                checked={preferences[index]?.sms.some(
                                  (smsOption) => smsOption.toLowerCase() === option.toLowerCase(),
                                )}
                                onChange={(e) =>
                                  updateCheckbox(
                                    index,
                                    NotificationPreferenceType.SMS,
                                    option,
                                    e.target.checked,
                                  )
                                }
                                disabled={isOptedOutSms || !getValues('phoneNumber')}
                              />
                              <span></span>
                            </label>
                            <label>
                              <input
                                type='checkbox'
                                checked={preferences[index]?.push.some(
                                  (pushOption) => pushOption.toLowerCase() === option.toLowerCase(),
                                )}
                                onChange={(e) =>
                                  updateCheckbox(
                                    index,
                                    NotificationPreferenceType.PUSH,
                                    option,
                                    e.target.checked,
                                  )
                                }
                              />
                              <span></span>
                            </label>
                          </CheckboxWrapper>
                        </Wrapper>
                      ))}
                    </div>
                  ) : null,
                )}
              </>
            )}
            {isMobileDevice && <MessageText marginLeft={-10} />}
          </div>
        </div>
      )}
    </RowDivider>
  );
};

export default UserNotificationsPreference;

const LabelWrapper = styled.label`
  color: ${theme.palette.wetAsphalt};
  white-space: nowrap;
  margin: 7px 0;
  font-size: ${theme.typography.fontSize14}px;
  margin-right: 15px;

  @media (min-width: 768px) {
    text-align: right;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;

  .notification-toggle {
    .toggle-switch {
      width: 45px;
      height: 21px;
    }
    .slider-input:checked + .slider-enabled {
      background-color: ${theme.palette.linkColor};
    }
    &.toggle-switch-container {
      margin: 0px 7px;
    }
  }
`;

type LeftWrapperProps = {
  isMobileDevice: boolean;
};

const LeftWrapper = styled.div<LeftWrapperProps>`
  .form-check-label {
    font-size: ${theme.typography.fontSize14}px !important;
  }
  .w-88 {
    width: ${(props) => (props.isMobileDevice ? '88%' : '92%')};
  }
`;

type MessageWrapperProps = {
  marginLeft?: number;
};

const MessageWrapper = styled.p<MessageWrapperProps>`
  font-size: ${theme.typography.h6}px !important;
  color: ${theme.palette.dusty};
  align-content: center;
  margin-left: ${(props) => (props.marginLeft ? props.marginLeft : 0)}px !important;
  margin-top: 10px;
`;

type StoreNameWrapperProps = {
  isMobileDevice: boolean;
};

export const StoreNameWrapper = styled.div<StoreNameWrapperProps>`
  font-weight: ${theme.typography.fontWeights.semibold};
  margin: 15px -10px;
  margin-block-start: ${(props) => (props.isMobileDevice ? '' : 'auto')};
`;

export const RadioButtonGroup = styled.div.attrs({
  className: 'nice-radiobuttons mb-0',
})`
  display: flex;
  margin-left: -10px;
  margin-bottom: 20px !important;

  label {
    font-weight: ${theme.typography.fontWeights.medium} !important;
    margin-right: 10px;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  margin: 5px -10px;
`;

const CheckboxLabelWrapper = styled.div`
  width: 50%;
  font-size: small;
`;

const CheckboxWrapper = styled.div.attrs({
  className: 'nice-checkboxes',
})`
  width: 50%;
  justify-content: space-around;
  display: flex;

  div {
    font-size: smaller;
    font-weight: ${theme.typography.fontWeights.semibold};
    margin: 0 10px;
  }
`;
